"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DmsTableBody = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireWildcard(require("prop-types"));
var _material = require("@mui/material");
var _reactIntlUniversal = _interopRequireDefault(require("react-intl-universal"));
var _DmsPermissions = require("../DmsPermissions/DmsPermissions");
var _is = require("../../service/utils/is");
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * Component for rendering the body of a table.
 * @param {Object} props - Component props.
 * @param {Array} props.data - Array of data rows to display.
 * @param {Array} props.header - Array of column headers.
 * @param {boolean} props.loading - Flag indicating if data is loading.
 * @returns {JSX.Element} Table body component.
 */
const DmsTableBody = _ref => {
  let {
    data,
    header,
    loading,
    selectOptions
  } = _ref;
  const padding = 7;
  /**
   * Component for displaying expandable text.
   * @param {Object} props - Component props.
   * @param {string} props.children - Text content.
   * @param {number} [props.maxChars=50] - Maximum characters to display before truncating.
   * @returns {JSX.Element} Expandable text component.
   */
  const Expandable = _ref2 => {
    let {
      children,
      maxChars = 50
    } = _ref2;
    const ellipsisStyle = {
      background: "none",
      border: "none"
    };
    const [expanded, setExpanded] = _react.default.useState(true);
    if (children.length <= maxChars) {
      return /*#__PURE__*/_react.default.createElement("p", null, children);
    }
    const text = expanded ? children.substring(0, maxChars) : children;
    return /*#__PURE__*/_react.default.createElement("div", null, text, /*#__PURE__*/_react.default.createElement("button", {
      style: ellipsisStyle,
      onClick: () => setExpanded(!expanded)
    }, "..."));
  };

  /**
   * Formats the item based on its type.
   * @param {any} item - Item to format.
   * @returns {string | React.ReactNode} Formatted item.
   */
  const formatItem = function (item) {
    let properties = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    if ([null, undefined, ""].includes(item)) {
      return "-";
    } else if (/*#__PURE__*/_react.default.isValidElement(item)) {
      return item;
    } else if (typeof item === "object" && properties && properties.column_name) {
      return Array.isArray(item) && item.length > 0 ? item[0][properties.column_name] : item[properties.column_name];
    } else if ((0, _is.isArray)(item)) {
      return item.join(", ");
    } else if (item === true) {
      return _reactIntlUniversal.default.get("Yes").d("Yes");
    } else if (item === false) {
      return _reactIntlUniversal.default.get("No").d("No");
    }
    return item;
  };
  return /*#__PURE__*/_react.default.createElement(_material.TableBody, null, loading ? /*#__PURE__*/_react.default.createElement(_material.TableRow, null, /*#__PURE__*/_react.default.createElement(_material.TableCell, {
    colSpan: header.length,
    align: "center"
  }, /*#__PURE__*/_react.default.createElement(_material.CircularProgress, null))) : data.length > 0 ? data.map((row, rowIndex) => /*#__PURE__*/_react.default.createElement(_material.TableRow, {
    key: rowIndex,
    "data-id": rowIndex
  }, header.map((col, colIndex) => {
    const {
      width,
      hidden,
      permissions,
      element,
      column_name,
      map,
      properties
    } = col;
    if (hidden || permissions && !(0, _DmsPermissions.havePermissions)(permissions)) {
      return null;
    }
    if (column_name === "endDate") {
      return null;
    }
    const cellKey = "".concat(rowIndex, "-").concat(colIndex);
    let cellContent = formatItem(row[column_name], properties);
    if (col.function) {
      cellContent = formatItem(col.function(row[column_name], row, column_name), properties);
      return /*#__PURE__*/_react.default.createElement(_material.TableCell, {
        key: cellKey,
        style: {
          width,
          padding
        }
      }, cellContent);
    } else if (map) {
      cellContent = formatItem(map[row[column_name]] || row[column_name], properties);
    } else if (properties) {
      let value = [];
      if (properties.multiple) {
        var _row$column_name;
        (_row$column_name = row[column_name]) === null || _row$column_name === void 0 || _row$column_name.forEach(item => {
          if (properties.column_name) {
            value.push(item[properties.column_name]);
          } else {
            Object.values(item).forEach(obj => value.push(obj));
          }
        });
      } else {
        value.push(row[column_name] || "-");
      }
      cellContent = /*#__PURE__*/_react.default.createElement(_material.TableCell, {
        key: cellKey,
        style: {
          width,
          padding
        }
      }, /*#__PURE__*/_react.default.createElement(Expandable, {
        maxChars: 50
      }, formatItem(value.join(", "), properties)));
      return cellContent;
    } else if (element) {
      const renderedElement = typeof element === "function" ? element(row) : element;
      if (/*#__PURE__*/_react.default.isValidElement(renderedElement)) {
        return /*#__PURE__*/_react.default.createElement(_material.TableCell, {
          key: cellKey,
          style: {
            width,
            padding
          }
        }, /*#__PURE__*/_react.default.cloneElement(renderedElement, {
          row
        }));
      } else {
        return /*#__PURE__*/_react.default.createElement(_material.TableCell, {
          key: cellKey,
          style: {
            width,
            padding
          }
        }, renderedElement);
      }
    }
    return /*#__PURE__*/_react.default.createElement(_material.TableCell, {
      key: cellKey,
      style: {
        width,
        padding
      }
    }, /*#__PURE__*/_react.default.isValidElement(cellContent) ? cellContent : /*#__PURE__*/_react.default.createElement(Expandable, {
      maxChars: 50
    }, String(cellContent)));
  }))) : /*#__PURE__*/_react.default.createElement(_material.TableRow, null, /*#__PURE__*/_react.default.createElement(_material.TableCell, {
    colSpan: header.length,
    align: "center",
    sx: {
      padding
    }
  }, _reactIntlUniversal.default.get("NoDataForThisTable").d("No data for this table"))));
};
exports.DmsTableBody = DmsTableBody;
DmsTableBody.propTypes = {
  data: _propTypes.default.arrayOf(_propTypes.default.object).isRequired,
  header: _propTypes.default.arrayOf(_propTypes.default.shape({
    column_name: _propTypes.default.string.isRequired,
    width: _propTypes.default.string,
    hidden: _propTypes.default.bool,
    permissions: _propTypes.default.arrayOf(_propTypes.default.string),
    element: _propTypes.default.oneOfType([_propTypes.default.node, _propTypes.default.func]),
    map: _propTypes.default.object,
    properties: _propTypes.default.shape({
      multiple: _propTypes.default.bool,
      column_name: _propTypes.default.string
    }),
    function: _propTypes.default.func
  })).isRequired,
  loading: _propTypes.default.bool.isRequired
};